export type TransformMatch = {
  match: true;
  transformedKey: string;
};

export type TransformNoMatch = {
  match: false;
};

export type LocalStorageKeyTransform = {
  read: (key: string) => TransformMatch | TransformNoMatch;
  write: (key: string) => TransformMatch | TransformNoMatch;
};

export const createACSLogsEnabledTransform = (configVersion: number): LocalStorageKeyTransform => {
  const baseKey = 'activeContractsLogsEnabled';
  const versionKey = `${baseKey}_ver:${configVersion}`;

  return {
    read: (key) => {
      // When we read key with version: e.g. `activeContractsLogsEnabled_ver:0`
      // we want to convert it `activeContractsLogsEnabled` as used in our types
      if (key === versionKey) {
        return { match: true, transformedKey: baseKey };
      }

      // If it is an unknown key we do nothing
      return { match: false };
    },
    write: (key) => {
      // Then to store it again in local storage we convert it from typed version (`activeContractsLogsEnabled`)
      // to versioned one .e.g. `activeContractsLogsEnabled_ver:0`
      if (key === baseKey) {
        return { match: true, transformedKey: versionKey };
      }

      // If it is an unknown key we do nothing
      return { match: false };
    },
  };
};

export const createLocalStorageAccessHelper = (transforms: LocalStorageKeyTransform[] = []) => {
  const readAll = () => {
    const result: Record<string, string | null> = {};

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);

      if (!key) {
        console.warn(`Failed to get key from localStorage at index: ${i}.`);
        // eslint-disable-next-line no-continue
        continue;
      }

      // We perform all known transforms on given key. If there is at least one match and transformed key, we get the first one.
      const transformedKey = transforms
        .map((transform) => transform.read(key))
        .filter((it): it is TransformMatch => it.match)
        .map((it) => it.transformedKey)
        .at(0);

      result[transformedKey ?? key] = localStorage.getItem(key);
    }

    return result;
  };

  const write = (key: string, value: string | number | boolean) => {
    // We perform all known transforms on given key. If there is at least one match and transformed key, we get the first one.
    const transformedKey = transforms
      .map((transform) => transform.write(key))
      .filter((it): it is TransformMatch => it.match)
      .map((it) => it.transformedKey)
      .at(0);

    localStorage.setItem(transformedKey ?? key, value.toString());
  };

  return {
    readAll,
    write,
  };
};
